import { getIconColour } from '../../utils/icons/icons';
import { svgReferences } from '../../utils/svg/svg';
import SvgIcon from '../SvgIcon/SvgIcon';
import './IconButton.scss';

/**
 * An icon button component.
 *
 * @param {string} [props.buttonStyleType] - String: The button type. Required, must be one of 'primary' or 'secondary'.
 * @param {string} [props.iconName] - String: The name of the icon to display. Required.
 * @param {boolean} [props.disabled=false] - Boolean: Whether the button is disabled. Optional, default is false.
 * @param {boolean} [props.small=false] - Boolean: Whether the button is small. Optional, default is false.
 * @param {function} props.handleClick - Function: The click handler for the button. Required.
 * @returns {JSX.Element} - The rendered button component.
 */

const IconButton = ({
  buttonStyleType,
  iconName,
  disabled = false,
  size = 'medium',
  handleClick,
  additionalStyling,
  iconColour,
  handleMouseOver,
  handleMouseLeave,
}: {
  buttonStyleType: 'primary' | 'secondary';
  iconName: keyof typeof svgReferences;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  handleClick: () => void;
  additionalStyling?: object;
  iconColour?: string;
  handleMouseOver?: () => void;
  handleMouseLeave?: () => void;
}) => {
  const ICON_SIZES = { small: 12, medium: 16, large: 24 };

  return (
    <button
      disabled={disabled}
      className={`icon-button ${size} ${buttonStyleType === 'secondary' ? 'icon-secondary' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={handleClick}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handleMouseOver}
      data-testid={`${iconName}-icon-button`}
      style={additionalStyling}
    >
      <SvgIcon
        data-testid={`${iconName}-svg`}
        iconName={iconName}
        fill={getIconColour(
          buttonStyleType,
          disabled,
          iconColour ? iconColour : buttonStyleType === 'secondary' ? 'var(--primary)' : undefined,
        )}
        width={ICON_SIZES[size]}
        height={ICON_SIZES[size]}
      />
    </button>
  );
};

export default IconButton;
